<script lang="ts">
	import { t } from 'svelte-i18n'
	import type {
		AbstractResource,
		Lever
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import { LeversTable, PageHeader, SetLeverValueModal } from '@repo/commons/components'
	import type { ProjectViewModel, TableData } from '@repo/commons/models'
	import {
		confirmDelete,
		DebouncedSearch,
		injectTranslation,
		showSaveFailedToast
	} from '@repo/commons/utils'

	type Props = {
		levers: readonly AbstractResource[]
		project: ProjectViewModel | null
		loading?: boolean
		sortable?: boolean
		onAdd: (lever: Partial<Lever>) => Promise<boolean>
		onEdit: (lever: Partial<Lever>) => Promise<boolean>
		onDelete: (leverId: string) => Promise<boolean>
	}

	const {
		levers,
		project,
		loading = false,
		sortable = false,
		onAdd,
		onEdit,
		onDelete
	}: Props = $props()

	const search = new DebouncedSearch()

	let editingLever = $state<TableData | null>(null)
	let isLeverModalVisible = $state(false)

	const leverRows: TableData[] = $derived(
		levers.map((resource) => ({ ...resource, name: resource.name ?? '' }))
	)

	const filteredLeverRows = $derived(search.filterRows(leverRows, ({ name }) => name))

	const existingLeverIds = $derived(leverRows.map(({ id }) => id))

	const emptyText = $derived(leverRows.length ? undefined : $t('InstanceLevers.empty'))

	const openAddLeverModal = (): void => {
		editingLever = null
		isLeverModalVisible = true
	}

	const openEditLeverModal = (row: TableData): void => {
		editingLever = row
		isLeverModalVisible = true
	}

	const closeLeverModal = (): void => {
		editingLever = null
		isLeverModalVisible = false
	}

	const saveLever = async (lever: Partial<Lever>, isEdit: boolean): Promise<boolean> => {
		const isSaved = await (isEdit ? onEdit(lever) : onAdd(lever))

		if (!isSaved) showSaveFailedToast()

		return isSaved
	}

	const confirmAndDeleteLever = async ({ id, name }: TableData): Promise<void> => {
		const isConfirmed = await confirmDelete(
			injectTranslation('Confirmation.deleteTitle', { item: $t('Item.lever') }),
			injectTranslation('Confirmation.deleteMessage', { item: $t('Item.lever'), name: name || id })
		)

		if (!isConfirmed) return

		const isDeleted = await onDelete(id)

		if (!isDeleted) showSaveFailedToast()
	}
</script>

<div class="content">
	<PageHeader
		title={$t('InstanceLevers.title')}
		addButtonText={$t('InstanceLevers.add')}
		addButtonIcon={null}
		disableAddButton={loading || !project}
		onAdd={openAddLeverModal}
		onSearch={search.updateQuery}
	/>

	<LeversTable
		rows={filteredLeverRows}
		{loading}
		{sortable}
		{emptyText}
		onEdit={openEditLeverModal}
		onDelete={confirmAndDeleteLever}
	/>
</div>

<SetLeverValueModal
	{project}
	{editingLever}
	{existingLeverIds}
	visible={isLeverModalVisible}
	onclose={closeLeverModal}
	onSubmit={saveLever}
/>

<style lang="postcss">
	.content {
		@apply flex flex-col gap-4;
	}
</style>
